import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Tab, Tabs, Typography } from '@material-ui/core';
import SortIcon from '@material-ui/icons/Sort';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import {
  getConversation,
  getReferenceUsers,
  markConversationOpened,
  getMailOrbitErrorKey
} from 'src/utils/MailOrbitService';
import useStyles, { statusDotColor } from './useStyles';
import MessageCard from './MessageCard';
import { sortMessagesByDate } from './format';
import CustomerFields from './CustomerFields';
import ReplyComposer from '../ReplyComposer';
import SenderHistory from './SenderHistory';
import WorkActions from '../NotesAndActions/WorkActions';
import InternalNotes from '../NotesAndActions/InternalNotes';

const PRIORITY_KEYS = {
  Low: 'priority_low',
  Normal: 'priority_normal',
  High: 'priority_high',
  Urgent: 'priority_urgent'
};

const priorityClass = (classes, priority) => {
  if (priority === 'Urgent') return classes.pillUrgent;
  if (priority === 'High') return classes.pillHigh;
  return classes.pillMuted;
};

const defaultMessage = messages => {
  const list = (messages || []).filter(m => !m.isMasked && !m.isDeleted);
  const inbound = list.filter(m => m.direction !== 'Outbound');
  // Sunucu sırası tarih sırası değildir; en yeni gelen mesaj tarihe göre bulunur.
  return sortMessagesByDate(inbound.length ? inbound : list, true)[0] || null;
};

/** Tek bir MailOrbit conversation'ı: başlık, mesaj zaman çizelgesi, müşteri alanları, gönderen geçmişi. */
const ConversationView = ({ conversationId, allowTerminalStatuses }) => {
  const classes = useStyles();
  const { t } = useTranslation(['mailorbit_workorder', 'mailorbit']);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [users, setUsers] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [openId, setOpenId] = useState(null); // varsayılan açık kart: son gelen mesaj
  const [newestFirst, setNewestFirst] = useState(true); // yalnızca bileşen durumu
  const [panel, setPanel] = useState('customer'); // 'customer' | 'notes' | 'history'
  const openedFor = useRef(null);

  const load = useCallback(async () => {
    const res = await getConversation(conversationId, { includeBodies: false });
    setDetail(res);
    return res;
  }, [conversationId]);

  const patchDetail = useCallback(patch => setDetail(d => (d ? { ...d, ...patch } : d)), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    setDetail(null);
    setSelectedId(null);
    setOpenId(null);
    setPanel('customer');
    (async () => {
      try {
        const res = await load();
        if (cancelled) return;
        const def = defaultMessage(res.messages);
        setSelectedId(def ? def.conversationMessageId : null);
        setOpenId(def ? def.conversationMessageId : null);
        setLoading(false);
        // Ekran açıldığında conversation başına BİR kez (yenilemede değil).
        if (openedFor.current !== conversationId) {
          openedFor.current = conversationId;
          try {
            const opened = await markConversationOpened(conversationId);
            if (!cancelled && opened && opened.statusChanged) await load();
          } catch (e) {
            // status otomasyonu başarısızsa görüntüleme engellenmez
          }
        }
      } catch (e) {
        if (!cancelled) {
          setErrorKey(getMailOrbitErrorKey(e));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, load]);

  useEffect(() => {
    let cancelled = false;
    getReferenceUsers()
      .then(list => {
        if (cancelled) return;
        const map = {};
        (list || []).forEach(u => {
          map[u.userId] = u.displayName;
        });
        setUsers(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box className={classes.center}>
        <CircularProgress />
      </Box>
    );
  }
  if (errorKey) {
    return (
      <Box className={classes.center}>
        <Alert severity="error">{t(`mailorbit:${errorKey}`)}</Alert>
      </Box>
    );
  }
  if (!detail) return null;

  const messages = detail.messages || [];
  const sortedMessages = sortMessagesByDate(messages, newestFirst);
  const selected = messages.find(m => m.conversationMessageId === selectedId) || null;
  const owner = detail.currentOwnerUserId
    ? users[detail.currentOwnerUserId] || '-'
    : t('mailorbit_workorder:unassigned');
  const priorityKey = PRIORITY_KEYS[detail.priority];

  const customerAvailable =
    !!selected && !selected.isMasked && !!selected.emailMessageMetadataId;

  return (
    <>
      <Box className={classes.header}>
        <Typography className={classes.headerTitle} title={detail.subject || ''}>
          <span className={classes.caseRef}>{detail.caseReference}</span>
          {detail.subject || ''}
        </Typography>
        <Box className={classes.headerMeta}>
          <span className={classes.badge}>
            <span
              className={classes.dot}
              style={{ backgroundColor: statusDotColor(detail.statusKey, detail.statusIsTerminal) }}
            />
            {detail.statusName || detail.statusKey || '-'}
          </span>
          <span className={`${classes.badge} ${priorityClass(classes, detail.priority)}`}>
            {priorityKey ? t(`mailorbit_workorder:${priorityKey}`) : detail.priority || '-'}
          </span>
          <Typography variant="body2" component="span" className={classes.metaOwner}>
            <span className={classes.metaLabel}>{t('mailorbit_workorder:owner')}</span>
            {owner}
          </Typography>
        </Box>
        <WorkActions
          conversationId={conversationId}
          detail={detail}
          onPatch={patchDetail}
          onRefresh={load}
          allowTerminalStatuses={allowTerminalStatuses}
        />
      </Box>
      <Box className={classes.body}>
        <Box className={classes.timeline}>
          <Box className={classes.timelineHead}>
            <Typography className={classes.timelineTitle}>{t('mailorbit_workorder:messages')}</Typography>
            <Button
              size="small"
              startIcon={<SortIcon fontSize="small" />}
              onClick={() => setNewestFirst(v => !v)}
              aria-label={t('mailorbit_workorder:sort_toggle')}
              title={t('mailorbit_workorder:sort_toggle')}
            >
              {t(newestFirst ? 'mailorbit_workorder:sort_newest' : 'mailorbit_workorder:sort_oldest')}
            </Button>
          </Box>
          {sortedMessages.map(m => (
            <MessageCard
              key={m.conversationMessageId}
              conversationId={conversationId}
              message={m}
              selected={m.conversationMessageId === selectedId}
              defaultExpanded={m.conversationMessageId === openId}
              users={users}
              ownerUserId={detail.currentOwnerUserId}
              isOwnedByMe={detail.isOwnedByMe === true}
              onSelect={msg => setSelectedId(msg.conversationMessageId)}
            />
          ))}
        </Box>
        <Box className={classes.side}>
          <Tabs
            value={panel}
            onChange={(e, v) => setPanel(v)}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            className={classes.sideTabs}
          >
            <Tab value="customer" label={t('mailorbit_workorder:tab_customer')} className={classes.sideTab} />
            <Tab value="notes" label={t('mailorbit_workorder:tab_notes')} className={classes.sideTab} />
            <Tab value="history" label={t('mailorbit_workorder:tab_history')} className={classes.sideTab} />
          </Tabs>
          {/* Sekme değişince bileşen kaldırılır; açılmış (reveal) değerler temizlenir. */}
          {panel === 'customer' &&
            (customerAvailable ? (
              <CustomerFields
                emailMessageId={selected.emailMessageMetadataId}
                canWork={detail.canWork === true}
              />
            ) : (
              <Box className={classes.sideSection}>
                <Typography variant="body2" color="textSecondary">
                  {t('mailorbit_workorder:no_customer_fields')}
                </Typography>
              </Box>
            ))}
          {panel === 'notes' && (
            <InternalNotes conversationId={conversationId} canWork={detail.canWork === true} />
          )}
          {panel === 'history' && <SenderHistory conversationId={conversationId} />}
        </Box>
      </Box>
      <Box className={classes.replyBar}>
        <ReplyComposer detail={detail} selectedMessage={selected} onSent={() => load().catch(() => {})} />
      </Box>
    </>
  );
};

ConversationView.propTypes = {
  conversationId: PropTypes.string.isRequired,
  // Bağlı açık iş emri yoksa true (SR-031); varsayılan: terminal durumlar sunulmaz.
  allowTerminalStatuses: PropTypes.bool
};

ConversationView.defaultProps = { allowTerminalStatuses: false };

export default ConversationView;
